import React, { useState, useEffect } from "react";
import { X, Clock, Moon, Plus, Trash2, Calendar } from "lucide-react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";

// ------------------------------------------------------------------
// Constants & Helpers
// ------------------------------------------------------------------

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const TIME_OPTIONS = [
  "12:00 AM",
  "12:30 AM",
  "1:00 AM",
  "1:30 AM",
  "2:00 AM",
  "2:30 AM",
  "3:00 AM",
  "3:30 AM",
  "4:00 AM",
  "4:30 AM",
  "5:00 AM",
  "5:30 AM",
  "6:00 AM",
  "6:30 AM",
  "7:00 AM",
  "7:30 AM",
  "8:00 AM",
  "8:30 AM",
  "9:00 AM",
  "9:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "1:00 PM",
  "1:30 PM",
  "2:00 PM",
  "2:30 PM",
  "3:00 PM",
  "3:30 PM",
  "4:00 PM",
  "4:30 PM",
  "5:00 PM",
  "5:30 PM",
  "6:00 PM",
  "6:30 PM",
  "7:00 PM",
  "7:30 PM",
  "8:00 PM",
  "8:30 PM",
  "9:00 PM",
  "9:30 PM",
  "10:00 PM",
  "10:30 PM",
  "11:00 PM",
  "11:30 PM",
];

/** Convert "HH:MM AM/PM" to total minutes since midnight */
const timeToMinutes = (t) => {
  const match = t?.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return 0;
  let h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  const meridiem = match[3].toUpperCase();
  if (meridiem === "PM" && h !== 12) h += 12;
  if (meridiem === "AM" && h === 12) h = 0;
  return h * 60 + m;
};

/** Convert minutes since midnight to "HH:MM AM/PM" */
const minutesToTime = (mins) => {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const meridiem = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, "0")} ${meridiem}`;
};

/** Format YYYY-MM-DD to DD-MM-YYYY for display */
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

/** (Unused but kept for consistency) */
const buildOpenSlotsFromRangeAndBreaks = (openFrom, openTo, breaks) => {
  const rangeStart = timeToMinutes(openFrom);
  const rangeEnd = timeToMinutes(openTo);
  if (rangeEnd <= rangeStart) return [];

  const sortedBreaks = (breaks || [])
    .map((b) => ({ start: timeToMinutes(b.from), end: timeToMinutes(b.to) }))
    .filter((b) => b.end > b.start)
    .sort((a, b) => a.start - b.start);

  const slots = [];
  let cursor = rangeStart;
  sortedBreaks.forEach((b) => {
    const bStart = Math.max(b.start, rangeStart);
    const bEnd = Math.min(b.end, rangeEnd);
    if (bStart > cursor) {
      slots.push({ from: minutesToTime(cursor), to: minutesToTime(bStart) });
    }
    cursor = Math.max(cursor, bEnd);
  });
  if (cursor < rangeEnd) {
    slots.push({ from: minutesToTime(cursor), to: minutesToTime(rangeEnd) });
  }
  return slots;
};

/** Reconstruct open/break from slot list (unused but kept) */
const reconstructRangeAndBreaks = (slots) => {
  if (!slots || slots.length === 0) {
    return { openFrom: "9:00 AM", openTo: "5:30 PM", breaks: [] };
  }
  const sorted = [...slots]
    .map((s) => ({ ...s, startMin: timeToMinutes(s.from), endMin: timeToMinutes(s.to) }))
    .sort((a, b) => a.startMin - b.startMin);

  const openFrom = sorted[0].from;
  const openTo = sorted[sorted.length - 1].to;
  const breaks = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i + 1].startMin > sorted[i].endMin) {
      breaks.push({ from: sorted[i].to, to: sorted[i + 1].from });
    }
  }
  return { openFrom, openTo, breaks };
};

/** Initial state: weekdays open, weekend closed */
const defaultHours = () =>
  DAY_NAMES.map((name, i) => ({
    name,
    open: i < 5,
    slots: [{ from: "9:00 AM", to: "5:30 PM" }],
  }));

// ------------------------------------------------------------------
// Sub-components: Toggle & TimeSelect
// ------------------------------------------------------------------

const Toggle = ({ checked, onChange, disabled }) => (
  <label
    className={`relative inline-flex items-center flex-shrink-0 ${
      disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
    }`}
  >
    <input
      type="checkbox"
      className="sr-only"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <div
      className={`w-10 h-[22px] rounded-full transition-colors duration-200 ${
        checked && !disabled ? "bg-indigo-600" : "bg-gray-300"
      }`}
    >
      <div
        className={`absolute top-[3px] left-[3px] w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
          checked && !disabled ? "translate-x-[18px]" : "translate-x-0"
        }`}
      />
    </div>
  </label>
);

const TimeSelect = ({
  value,
  onChange,
  className = "w-32",
  disabled,
  minTime,
}) => {
  const minMinutes = minTime ? timeToMinutes(minTime) : null;

  const availableOptions = TIME_OPTIONS.filter((t) => {
    if (minMinutes === null) return true;
    return timeToMinutes(t) > minMinutes;
  });

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`${className} px-2.5 py-1.5 border border-gray-200 rounded-md text-sm text-gray-700 focus:ring-1 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed`}
    >
      {availableOptions.map((t) => (
        <option key={t}>{t}</option>
      ))}
    </select>
  );
};

// ------------------------------------------------------------------
// Main Component
// ------------------------------------------------------------------

/**
 * BusinessHoursDrawer
 * @param {boolean} isOpen - controls drawer visibility
 * @param {function} onClose - close drawer callback
 * @param {function} onSave - optional callback after successful save
 * @param {string} type - "Grooming" or "Vet" to choose the API endpoints
 * @param {string} vendorId - vendor ID for marking step completion (required for onboarding)
 */
const BusinessHoursDrawer = ({
  isOpen,
  onClose,
  onSave,
  type = "Grooming",
  vendorId, // 👈 NEW: needed to mark business_hours step completed
}) => {
  // --- State ---
  const [days, setDays] = useState(defaultHours);
  const [selectedChips, setSelectedChips] = useState([0, 1, 2, 3, 4]);
  const [globalSlots, setGlobalSlots] = useState([
    { from: "9:00 AM", to: "5:30 PM" },
  ]);
  const [specialDays, setSpecialDays] = useState([]);

  const isDuplicateSpecialDate = (date, currentIndex = -1) => {
    return specialDays.some(
      (day, index) => index !== currentIndex && day.date === date,
    );
  };

  // ── endpoint config based on type ─────────────────────────────────────
  const isVet = type === "Vet";
  const config = isVet
    ? {
        getUrl: "/vethours/getvetbusinesshours",
        putUrl: "/vethours/updatevetbusinesshours",
        refreshKey: "refreshVetBusinessHours",
        label: "vet",
      }
    : {
        getUrl: "/groominghours/getgroomingbusinesshours",
        putUrl: "/groominghours/updategroomingbusinesshours",
        refreshKey: "refreshBusinessHours",
        label: "grooming",
      };

  // ✅ SPECIAL DAYS FUNCTIONS - ADD THESE
  const addSpecialDay = () => {
    const today = new Date().toISOString().split("T")[0];
    setSpecialDays((prev) => [
      ...prev,
      { date: today, dayStatus: "closed", slots: [] },
    ]);
  };

  const removeSpecialDay = (index) => {
    setSpecialDays(specialDays.filter((_, i) => i !== index));
  };

  const updateSpecialDay = (index, field, value) => {
    setSpecialDays((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)),
    );
  };

  const updateSpecialDayStatus = (index, dayStatus) => {
    setSpecialDays((prev) =>
      prev.map((d, i) => {
        if (i !== index) return d;
        const shouldSeed =
          dayStatus === "custom" && (!d.slots || d.slots.length === 0);
        return {
          ...d,
          dayStatus,
          slots: shouldSeed
            ? [{ from: "9:00 AM", to: "5:30 PM", status: null }]
            : d.slots,
        };
      }),
    );
  };

  const addSpecialDaySlot = (index) => {
    setSpecialDays((prev) =>
      prev.map((d, i) =>
        i === index
          ? {
              ...d,
              slots: [
                ...(d.slots || []),
                { from: "9:00 AM", to: "1:00 PM", status: null },
              ],
            }
          : d,
      ),
    );
  };

  const removeSpecialDaySlot = (index, slotIdx) => {
    setSpecialDays((prev) =>
      prev.map((d, i) =>
        i === index
          ? { ...d, slots: d.slots.filter((_, j) => j !== slotIdx) }
          : d,
      ),
    );
  };

  const updateSpecialDaySlot = (index, slotIdx, field, value) => {
    setSpecialDays((prev) =>
      prev.map((d, i) =>
        i === index
          ? {
              ...d,
              slots: d.slots.map((s, j) =>
                j === slotIdx ? { ...s, [field]: value } : s,
              ),
            }
          : d,
      ),
    );
  };

  // --- Weekday chips logic ---
  const allSelected = selectedChips.length === 7;

  const handleToggleChip = (i) => {
    setSelectedChips((prev) => {
      const nextChips = prev.includes(i)
        ? prev.filter((d) => d !== i)
        : [...prev, i];

      setDays((prevDays) =>
        prevDays.map((d, idx) =>
          idx === i ? { ...d, open: nextChips.includes(i) } : d,
        ),
      );
      return nextChips;
    });
  };

  const toggleSelectAll = () => {
    const nextChips = allSelected ? [] : DAY_LABELS.map((_, i) => i);
    setSelectedChips(nextChips);
    setDays((prev) => prev.map((d, i) => ({ ...d, open: !allSelected })));
  };

  const toggleDay = (i, val) => {
    setDays((prev) =>
      prev.map((d, idx) => (idx === i ? { ...d, open: val } : d)),
    );
    setSelectedChips((prev) =>
      val ? [...prev, i] : prev.filter((d) => d !== i),
    );
  };

  // --- Day slots manipulation ---
  const updateSlot = (dayIdx, slotIdx, field, val) =>
    setDays((prev) =>
      prev.map((d, i) =>
        i === dayIdx
          ? {
              ...d,
              slots: d.slots.map((s, j) =>
                j === slotIdx ? { ...s, [field]: val } : s,
              ),
            }
          : d,
      ),
    );

  const addSlot = (dayIdx) =>
    setDays((prev) =>
      prev.map((d, i) =>
        i === dayIdx
          ? { ...d, slots: [...d.slots, { from: "2:00 PM", to: "6:00 PM" }] }
          : d,
      ),
    );

  const removeSlot = (dayIdx, slotIdx) =>
    setDays((prev) =>
      prev.map((d, i) =>
        i === dayIdx
          ? { ...d, slots: d.slots.filter((_, j) => j !== slotIdx) }
          : d,
      ),
    );

  // --- Global slots (applied to all selected days) ---
  const updateGlobalSlot = (slotIdx, field, val) => {
    const updatedGlobal = globalSlots.map((s, j) =>
      j === slotIdx ? { ...s, [field]: val } : s,
    );
    setGlobalSlots(updatedGlobal);

    setDays((prev) =>
      prev.map((day, dIdx) =>
        selectedChips.includes(dIdx)
          ? { ...day, slots: JSON.parse(JSON.stringify(updatedGlobal)) }
          : day,
      ),
    );
  };

  const addGlobalSlot = () => {
    const updatedGlobal = [...globalSlots, { from: "2:00 PM", to: "6:00 PM" }];
    setGlobalSlots(updatedGlobal);

    setDays((prev) =>
      prev.map((day, dIdx) =>
        selectedChips.includes(dIdx)
          ? { ...day, slots: JSON.parse(JSON.stringify(updatedGlobal)) }
          : day,
      ),
    );
  };

  const removeGlobalSlot = (slotIdx) => {
    const updatedGlobal = globalSlots.filter((_, j) => j !== slotIdx);
    setGlobalSlots(updatedGlobal);

    setDays((prev) =>
      prev.map((day, dIdx) =>
        selectedChips.includes(dIdx)
          ? { ...day, slots: JSON.parse(JSON.stringify(updatedGlobal)) }
          : day,
      ),
    );
  };

  // --- Load existing business hours from API ---
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        const res = await httpService.get(config.getUrl);
        const data = res.data?.data || res.data;
        if (!data) return;

        // Populate weekly schedule
        if (data.weeklySchedule?.length) {
          const openDays = data.weeklySchedule
            .map((d, i) => (d.isOpen ? i : -1))
            .filter((i) => i >= 0);
          setSelectedChips(openDays);
          setDays(
            DAY_NAMES.map((name, i) => ({
              name,
              open: data.weeklySchedule[i]?.isOpen ?? i < 5,
              slots: data.timeSlots?.map((s) => ({
                from: s.start,
                to: s.end,
              })) || [{ from: "9:00 AM", to: "5:30 PM" }],
            })),
          );
        }

        // Populate time slots
        if (data.timeSlots?.length) {
          setGlobalSlots(
            data.timeSlots.map((s) => ({ from: s.start, to: s.end })),
          );
        }

        // Populate special days
        if (data.specialDays?.length) {
          setSpecialDays(
            data.specialDays.map((sd) => {
              const dayStatus =
                sd.label === "Closed"
                  ? "closed"
                  : sd.label === "Full Day"
                    ? "full"
                    : "custom";

              let slots = [];
              if (dayStatus === "custom") {
                slots = (sd.slots || []).map((s) => ({
                  from: s.start,
                  to: s.end,
                  status: s.status || (sd.isOpen ? "open" : "closed"),
                }));
              }

              return {
                id: sd._id || sd.id,
                date: sd.date ? sd.date.split("T")[0] : "",
                dayStatus,
                slots,
              };
            }),
          );
        } else {
          setSpecialDays([]);
        }
      } catch (err) {
        console.error("Failed to load business hours", err);
      }
    };
    load();
  }, [isOpen, config.getUrl]);

  // --- Mark business_hours step as completed when drawer opens ---
  useEffect(() => {
    if (isOpen && vendorId) {
      const markStepOnVisit = async () => {
        try {
          await httpService.put(`/vendorregistration/updatesetup/${vendorId}`, {
            steps: [{ key: "business_hours", completed: true }],
          });
          console.log(
            ` Business hours step marked complete on visit for vendor ${vendorId}`,
          );
        } catch (error) {
          console.error(
            "Failed to mark business hours step on visit:",
            error,
          );
        }
      };
      markStepOnVisit();
    }
  }, [isOpen, vendorId]);

  // --- Save handler (with setup step completion) ---
  const handleSave = async () => {
    // 1. Validate duplicate special dates
    const duplicateDates = specialDays.filter(
      (day, index, self) =>
        self.findIndex((d) => d.date === day.date) !== index,
    );
    if (duplicateDates.length > 0) {
      const duplicateDate = duplicateDates[0].date;
      toast.error(
        `Duplicate date in Special Days found: ${formatDateForDisplay(
          duplicateDate,
        )}. Please keep only one entry for each date.`,
      );
      return;
    }

    // 2. Validate custom slots have status set
    const hasUnsetStatus = specialDays.some(
      (sd) => sd.dayStatus === "custom" && sd.slots?.some((s) => !s.status),
    );
    if (hasUnsetStatus) {
      toast.error("Please select Open or Closed for all custom hour slots");
      return;
    }

    // 3. Build payload for business hours
    const payload = {
      timeSlots: globalSlots.map((s) => ({ start: s.from, end: s.to })),
      weeklySchedule: days.map((d) => ({ day: d.name, isOpen: d.open })),
      specialDays: specialDays.map((sd) => {
        let finalSlots = [];
        let isOpen = false;

        if (sd.dayStatus === "full") {
          finalSlots = globalSlots.map((s) => ({ start: s.from, end: s.to }));
          isOpen = true;
        } else if (sd.dayStatus === "closed") {
          finalSlots = [];
          isOpen = false;
        } else if (sd.dayStatus === "custom") {
          finalSlots = (sd.slots || []).map((s) => ({
            start: s.from,
            end: s.to,
            status: s.status || "open",
          }));
          isOpen = finalSlots.some((s) => s.status === "open");
        }

        return {
          ...(sd.id ? { id: sd.id } : {}),
          date: sd.date,
          label:
            sd.dayStatus === "closed"
              ? "Closed"
              : sd.dayStatus === "full"
                ? "Full Day"
                : "Custom Hours",
          isOpen,
          slots: finalSlots,
        };
      }),
    };

    try {
      // await httpService.put("/groominghours/updategroomingbusinesshours", payload);
      // toast.success("Business hours saved!");
      //if (window.refreshBusinessHours) window.refreshBusinessHours();
      await httpService.put(config.putUrl, payload);
      toast.success("Business hours saved!");

      // 5. 👇 NEW: Mark the "business_hours" step as completed in vendor setup
      //    Only if vendorId is provided (i.e., we're in the onboarding flow)
      if (vendorId) {
        try {
          await httpService.put(`/vendorregistration/updatesetup/${vendorId}`, {
            steps: [{ key: "business_hours", completed: true }],
          });
          console.log(
            `✅ Business hours step marked complete for vendor ${vendorId}`,
          );
        } catch (stepErr) {
          // Log error but don't block the main success – the user can retry later
          console.error(
            "❌ Failed to mark business hours step complete:",
            stepErr,
          );
          // Optionally notify the user with a warning
          toast.warn(
            "Business hours saved, but we couldn't update your onboarding progress. Please refresh and try again.",
          );
        }
      }

      // 6. Notify parent (close drawer, refresh data, etc.)
      if (typeof onSave === "function") {
        onSave();
      } else {
        onClose();
      }

      // Populate special days
      // if (data.specialDays?.length) {
      //   setSpecialDays(data.specialDays.map((sd) => ({
      //     id: sd._id || sd.id,
      //     date: sd.date,
      //     label: sd.label || "",
      //     enabled: sd.isOpen,
      //     slots: sd.slots?.map((s) => ({ from: s.start, to: s.end })) || [],
      //   })),
      //   );
      // }else {
      //   setSpecialDays([]); // reset when switching type so vet doesn't show stale grooming special days
      // }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save business hours.");
    }
  };

  // --- Render: if not open, return null ---
  if (!isOpen) return null;

  // --- JSX ---
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-1/2 bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <Clock className="w-5 h-5 text-indigo-600" />
            <h2 className="text-base font-semibold text-gray-800">
              Set Business Hours
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <p className="text-xs text-gray-500">
            Set your business hours for {config.label} appointments.
          </p>

          {/* Day chips */}
          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-2.5">
              Select days of the week
            </p>
            <div className="flex gap-2 mb-3">
              {DAY_LABELS.map((label, i) => (
                <button
                  key={i}
                  onClick={() => handleToggleChip(i)}
                  className={`w-9 h-9 rounded-full text-xs font-medium transition-colors ${
                    selectedChips.includes(i)
                      ? "bg-indigo-600 text-white"
                      : "border border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="accent-indigo-600 w-3.5 h-3.5"
              />
              <span className="text-xs font-medium text-indigo-600">
                Select all days
              </span>
            </label>
          </div>

          {/* Global Multi-Slot Scheduler */}
          <div className="p-4 space-y-4">
            <div className="space-y-3">
              {globalSlots.map((slot, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-8">From</span>
                  <TimeSelect
                    value={slot.from}
                    onChange={(val) => updateGlobalSlot(index, "from", val)}
                  />
                  <span className="text-gray-400 px-1">–</span>
                  <span className="text-xs text-gray-400 w-6">To</span>
                  <TimeSelect
                    value={slot.to}
                    minTime={slot.from}
                    onChange={(val) => updateGlobalSlot(index, "to", val)}
                  />

                  <div className="flex items-center gap-3 ml-4">
                    {index === globalSlots.length - 1 && (
                      <button
                        onClick={addGlobalSlot}
                        className="flex items-center gap-1.5 text-xs text-indigo-600 bg-white border border-indigo-100 hover:bg-indigo-50 active:bg-indigo-100 transition-colors px-3 py-1.5 rounded-full font-medium whitespace-nowrap shadow-sm"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add slot
                      </button>
                    )}
                    {globalSlots.length > 1 && (
                      <button
                        onClick={() => removeGlobalSlot(index)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Main Table */}
          {/* Main Table - Week Days */}
          {/*<div className="border border-gray-100 rounded-xl divide-y divide-gray-100 overflow-hidden">
            {days.map((day, i) => {
              const isChipActive = selectedChips.includes(i);
              return (
                <div
                  key={i}
                  className={`flex items-start gap-4 p-4 transition-colors ${
                    isChipActive ? "bg-white" : "bg-gray-50/80 opacity-60"
                  }`}
                >
                  {/* Toggle + Day name */}
          {/* <div className="flex items-center gap-2.5 pt-1.5 w-32 flex-shrink-0">
                    <Toggle
                      checked={day.open}
                      onChange={(e) => toggleDay(i, e.target.checked)}
                    />
                    <span
                      className={`text-sm font-medium ${isChipActive ? "text-gray-800" : "text-gray-400"}`}
                    >
                      {day.name}
                    </span>
                  </div>

                  {/* Slots configuration */}
          {/*  {day.open ? (
                    <div className="flex-1 space-y-3">
                      {day.slots.map((slot, j) => (
                        <div key={j} className="flex items-center gap-3">
                          <span className="text-xs text-gray-400 w-8">
                            From
                          </span>
                          <TimeSelect
                            value={slot.from}
                            onChange={(v) => updateSlot(i, j, "from", v)}
                          />
                          <span className="text-gray-400 px-1">–</span>
                          <span className="text-xs text-gray-400 w-6">To</span>
                          <TimeSelect
                            value={slot.to}
                            onChange={(v) => updateSlot(i, j, "to", v)}
                          />

                          <div className="flex items-center gap-3 ml-4">
                            {j === day.slots.length - 1 && (
                              <button
                                onClick={() => addSlot(i)}
                                className="flex items-center gap-1.5 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 active:bg-indigo-200 transition-colors px-3 py-1.5 rounded-full font-medium whitespace-nowrap shadow-sm"
                              >
                                <Plus className="w-3.5 h-3.5" /> Add slot
                              </button>
                            )}
                            {day.slots.length > 1 && (
                              <button
                                onClick={() => removeSlot(i, j)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/60 border border-gray-100 rounded-md text-xs text-gray-400 flex-1 max-w-[200px]">
                      <Moon className="w-3.5 h-3.5" /> Closed
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ✅ SPECIAL DAYS SECTION - ADD THIS ENTIRE BLOCK */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-600" />
                <h3 className="text-sm font-semibold text-gray-700">
                  Special Days
                </h3>
                <span className="text-xs text-gray-400">
                  (Override regular hours)
                </span>
              </div>
              <button
                onClick={addSpecialDay}
                className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors px-3 py-1.5 rounded-full"
              >
                <Plus className="w-3.5 h-3.5" /> Add Special Day
              </button>
            </div>

            {specialDays.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">
                  No special days added yet.
                </p>
                <p className="text-xs text-gray-400">
                  Click "Add Special Day" to set custom hours.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {specialDays.map((specialDay, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm"
                  >
                    {/* Special Day Header */}
                    {/* <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Toggle
                          checked={specialDay.enabled}
                          onChange={(e) =>
                            toggleSpecialDay(index, e.target.checked)
                          }
                        />
                        <input
                          type="date"
                          value={specialDay.date}
                          onChange={(e) =>
                            updateSpecialDay(index, "date", e.target.value)
                          }
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          value={specialDay.label}
                          onChange={(e) =>
                            updateSpecialDay(index, "label", e.target.value)
                          }
                          placeholder="e.g., Christmas Day"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-indigo-500 focus:border-transparent flex-1 min-w-[150px]"
                        />
                      </div>
                      <button
                        onClick={() => removeSpecialDay(index)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div> */}
                    {/* Special Day Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="date"
                          min={new Date().toISOString().split("T")[0]}
                          value={specialDay.date}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            if (isDuplicateSpecialDate(newDate, index)) {
                              toast.warning(
                                "This date is already added as a special day.",
                              );
                              return;
                            }
                            updateSpecialDay(index, "date", newDate);
                          }}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                        />

                        <select
                          value={specialDay.dayStatus}
                          onChange={(e) =>
                            updateSpecialDayStatus(index, e.target.value)
                          }
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-1 focus:ring-indigo-500 focus:border-transparent bg-white cursor-pointer"
                        >
                          <option value="closed">Closed All Day</option>
                          <option value="full">Open All Day</option>
                          <option value="custom">Custom Hours</option>
                        </select>
                      </div>
                      <button
                        onClick={() => removeSpecialDay(index)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Custom Hours: slot list with Open/Closed toggle per slot */}
                    {specialDay.dayStatus === "custom" && (
                      <div className="ml-2 space-y-2">
                        {(specialDay.slots || []).map((slot, slotIdx) => (
                          <div
                            key={slotIdx}
                            className="flex items-center gap-2"
                          >
                            <span className="text-xs text-gray-500 w-8">
                              From
                            </span>
                            <TimeSelect
                              value={slot.from}
                              onChange={(v) =>
                                updateSpecialDaySlot(index, slotIdx, "from", v)
                              }
                            />
                            <span className="text-gray-400 px-1">–</span>
                            <span className="text-xs text-gray-500 w-6">
                              To
                            </span>
                            <TimeSelect
                              value={slot.to}
                              minTime={slot.from}
                              onChange={(v) =>
                                updateSpecialDaySlot(index, slotIdx, "to", v)
                              }
                            />
                            {slotIdx === specialDay.slots.length - 1 && (
                              <button
                                onClick={() => addSpecialDaySlot(index)}
                                className="flex items-center gap-1.5 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors px-3 py-1.5 rounded-full font-medium whitespace-nowrap"
                              >
                                <Plus className="w-3.5 h-3.5" /> Add slot
                              </button>
                            )}
                            {specialDay.slots.length > 1 && (
                              <button
                                onClick={() =>
                                  removeSpecialDaySlot(index, slotIdx)
                                }
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {!specialDay.enabled && (
                      <div className="ml-14 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md text-xs text-gray-400">
                        <Moon className="w-3.5 h-3.5" /> Closed on this day
                      </div>
                    )}{" "}
                   
                    {/* Custom Hours: open range + optional break periods */}
                    {/* Custom Hours: one slot list, each row states its own Open/Closed status */}
                    {specialDay.dayStatus === "custom" && (
                      <div className="ml-2 space-y-2">
                        {(specialDay.slots || []).map((slot, slotIdx) => (
                          <div
                            key={slotIdx}
                            className="flex items-center gap-2"
                          >
                            <span className="text-xs text-gray-500 w-8">
                              From
                            </span>
                            <TimeSelect
                              value={slot.from}
                              onChange={(v) =>
                                updateSpecialDaySlot(index, slotIdx, "from", v)
                              }
                            />
                            <span className="text-gray-400 px-1">–</span>
                            <span className="text-xs text-gray-500 w-6">
                              To
                            </span>
                            <TimeSelect
                              value={slot.to}
                              minTime={slot.from}
                              onChange={(v) =>
                                updateSpecialDaySlot(index, slotIdx, "to", v)
                              }
                            />

                            {/* Per-slot Open/Closed segmented toggle — neither selected until clicked */}
                            <div className="flex rounded-md overflow-hidden border border-gray-700 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() =>
                                  updateSpecialDaySlot(
                                    index,
                                    slotIdx,
                                    "status",
                                    "open",
                                  )
                                }
                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                                  slot.status === "open"
                                    ? "bg-emerald-500 text-white"
                                    : "bg-white text-gray-600 hover:bg-gray-50"
                                }`}
                              >
                                Open
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  updateSpecialDaySlot(
                                    index,
                                    slotIdx,
                                    "status",
                                    "closed",
                                  )
                                }
                                className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-700 ${
                                  slot.status === "closed"
                                    ? "bg-red-500 text-white"
                                    : "bg-white text-gray-600 hover:bg-gray-50"
                                }`}
                              >
                                Closed
                              </button>
                            </div>

                            <div className="flex items-center gap-2 ml-2">
                              {slotIdx === specialDay.slots.length - 1 && (
                                <button
                                  onClick={() => addSpecialDaySlot(index)}
                                  className="flex items-center gap-1.5 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors px-3 py-1.5 rounded-full font-medium whitespace-nowrap"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add slot
                                </button>
                              )}
                              {specialDay.slots.length > 1 && (
                                <button
                                  onClick={() =>
                                    removeSpecialDaySlot(index, slotIdx)
                                  }
                                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* {specialDay.slots.length > 0 && (
                          <p className="text-[11px] text-gray-400">
                            Open: {specialDay.slots.filter((s) => s.status === "open").map((s) => `${s.from}–${s.to}`).join(", ") || "none"}
                            {" · "}
                            Closed: {specialDay.slots.filter((s) => s.status === "closed").map((s) => `${s.from}–${s.to}`).join(", ") || "none"}
                          </p>
                        )} */}
                      </div>
                    )}
                    {specialDay.dayStatus === "full" && (
                      <div className="ml-10 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-md text-xs text-emerald-600">
                        <Clock className="w-3.5 h-3.5" /> Open all day
                      </div>
                    )}
                    {specialDay.dayStatus === "closed" && (
                      <div className="ml-10 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-md text-xs text-red-600">
                        <Moon className="w-3.5 h-3.5" /> Closed on this day
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2.5 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Save changes
          </button>
        </div>
      </div>
    </>
  );
};;

export default BusinessHoursDrawer;